import { Link, useLocation, useParams, useNavigate } from 'react-router-dom';
import { useEffect } from 'react';

import { BackButton } from '@/components/stashstr/BackButton';
import { Feed } from '@/components/stashstr/Feed';
import {
  TOP_TIMEFRAMES,
  TOP_METRICS,
  parseTopParams,
  timeframeToSince,
  type TopTimeframe,
  type TopMetric,
} from '@/hooks/useStashstrFeed';
import { cn } from '@/lib/utils';

export default function Top() {
  const { timeframe, metric } = useParams<{ timeframe?: string; metric?: string }>();
  const location = useLocation();
  const navigate = useNavigate();

  const parsed = parseTopParams(timeframe, metric);

  // Canonicalize invalid/missing segments: /top → /top/day/zaps etc.
  const canonical = `/top/${parsed.timeframe}/${parsed.metric}`;
  useEffect(() => {
    if (location.pathname !== canonical) {
      navigate(canonical, { replace: true });
    }
  }, [location.pathname, canonical, navigate]);

  return (
    <div>
      <BackButton />
      <h1 className="text-[18px] font-bold text-gray-900 dark:text-gray-100 mb-2">top</h1>
      <FilterRows
        basePath="/top"
        timeframe={parsed.timeframe}
        metric={parsed.metric}
        labels={{ period: 'period:', rank: 'rank by:' }}
      />
      <Feed sort="top" since={timeframeToSince(parsed.timeframe)} topMetric={parsed.metric} />
    </div>
  );
}

export function FilterRows({
  basePath,
  timeframe,
  metric,
  labels,
}: {
  basePath: string;
  timeframe: TopTimeframe;
  metric: TopMetric;
  labels?: { period?: string; rank?: string };
}) {
  const segment = (active: boolean): string =>
    cn(
      'px-2 py-0.5 rounded text-[12px]',
      active
        ? 'font-bold text-[#ea580c] dark:text-[#fdba74] underline underline-offset-2'
        : 'text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200',
    );

  return (
    <div className="space-y-1 mb-4" role="navigation" aria-label="Filters">
      <div className="flex flex-wrap gap-1 items-center">
        <span className="text-[12px] text-gray-400 dark:text-gray-500 mr-1">
          {labels?.period ?? 'period:'}
        </span>
        {TOP_TIMEFRAMES.map((tf) => (
          <Link key={tf} to={`${basePath}/${tf}/${metric}`} className={segment(tf === timeframe)}>
            {tf}
          </Link>
        ))}
      </div>
      <div className="flex flex-wrap gap-1 items-center">
        <span className="text-[12px] text-gray-400 dark:text-gray-500 mr-1">
          {labels?.rank ?? 'rank by:'}
        </span>
        {TOP_METRICS.map((m) => (
          <Link key={m} to={`${basePath}/${timeframe}/${m}`} className={segment(m === metric)}>
            {m}
          </Link>
        ))}
      </div>
    </div>
  );
}
